export type GradingProvider = 'PSA' | 'TAG' | 'CGC'

export const GRADING_PROVIDERS: GradingProvider[] = ['PSA', 'TAG', 'CGC']

export interface ProviderGradeResult {
  condition_type: string
  grade: number
  /** Maps grade key (e.g. "9", "10", "11" for CGC 10P) to probability 0–1 */
  confidence_map: Record<string, number>
}

export interface ClosestMatchSummary {
  version?: string | number
  provider?: string
  grade?: number | string
  cert_number?: string
  psa_url?: string
  score?: number
  match_percent?: number
  match_percentages?: Record<string, number | undefined>
}

export interface MostSimilarGrade {
  id: string
  provider: string
  cert_number: string
  title?: string
  subject?: string
  psa_url?: string
  official_front_url?: string
  official_back_url?: string
  actual_grade?: number
  grade_label?: string
  model_tag?: string
  front_file_url?: string
  back_file_url?: string
  front_processed_file_url?: string
  back_processed_file_url?: string
  display_front_processed_file_url?: string
  display_back_processed_file_url?: string
  front_thumbnail_file_url?: string
  back_thumbnail_file_url?: string
  display_front_overlay_file_url?: string
  display_back_overlay_file_url?: string
  front_overlay_file_url?: string
  back_overlay_file_url?: string
  front_left_grade?: number
  front_right_grade?: number
  front_top_grade?: number
  front_bottom_grade?: number
  back_left_grade?: number
  back_right_grade?: number
  back_top_grade?: number
  back_bottom_grade?: number
  front_border_width_ratio_h?: number
  front_border_width_ratio_v?: number
  back_border_width_ratio_h?: number
  back_border_width_ratio_v?: number
  front_border_coords?: Partial<Record<BorderEdge, number[] | null>>
  back_border_coords?: Partial<Record<BorderEdge, number[] | null>>
  front_border_error_sides?: string[]
  back_border_error_sides?: string[]
  front_left_outer_ratio?: number
  front_right_outer_ratio?: number
  front_top_outer_ratio?: number
  front_bottom_outer_ratio?: number
  back_left_outer_ratio?: number
  back_right_outer_ratio?: number
  back_top_outer_ratio?: number
  back_bottom_outer_ratio?: number
  front_left_inner_ratio?: number
  front_right_inner_ratio?: number
  front_top_inner_ratio?: number
  front_bottom_inner_ratio?: number
  back_left_inner_ratio?: number
  back_right_inner_ratio?: number
  back_top_inner_ratio?: number
  back_bottom_inner_ratio?: number
  front_surface_damage_count?: number
  back_surface_damage_count?: number
  front_surface_damage_point_count?: number
  back_surface_damage_point_count?: number
  damage_detections?: DamageDetection[] | null
  border_detections?: BorderDetection[] | null
  front_mask_paths?: string[]
  back_mask_paths?: string[]
  front_mask_source_width?: number
  front_mask_source_height?: number
  back_mask_source_width?: number
  back_mask_source_height?: number
  centering?: { front: CenteringSide; back: CenteringSide }
  analysis?: { front: GradeSideAnalysis; back: GradeSideAnalysis }
}

/** Centering ratios, 0–1 scale where 0.5 is perfectly centered */
export interface CenteringSide {
  top: number
  bottom: number
  left: number
  right: number
}

export interface ImperfectionsSide {
  count: number
  /** Affected surface area, percent (e.g. 0.42 = 0.42%) */
  areaPct: number
}

/**
 * Profit inputs are pre-resolved server-side (the app's override logic
 * runs there) — the page only does the arithmetic:
 *   net = gradePrice − gradingFee; profit = net − nearMintPrice
 */
export interface ProfitInputs {
  /** Expected sale price per grade key */
  gradePrices: Record<string, number | null>
  nearMintPrice: number | null
  gradingFee: number
}

export interface DamageBox {
  x1: number
  y1: number
  x2: number
  y2: number
}

export interface DamageDetection {
  id: string
  side?: 'front' | 'back'
  type?: string
  box: DamageBox
  crop_box?: DamageBox
  area?: number
  is_corner?: boolean
  confidence?: number
  raw_file_url?: string
  emboss_file_url?: string
  thumbnail_file_url?: string
}

export type BorderEdge = 'left' | 'right' | 'top' | 'bottom'

export interface BorderLine {
  x1: number
  y1: number
  xm?: number
  ym?: number
  x2: number
  y2: number
  curve_kind?: 'piecewise_linear'
  sampled_points?: [number, number][]
}

export interface ImageRectification {
  version: 1
  status: 'applied'
  source: string
  source_coordinate_space: 'source_pixels'
  display_coordinate_space: 'rectified_pixels'
  source_shape: [number, number]
  display_shape: [number, number]
  homography: [
    [number, number, number],
    [number, number, number],
    [number, number, number],
  ]
  inverse_homography?: [
    [number, number, number],
    [number, number, number],
    [number, number, number],
  ]
  source_quad?: [number, number][]
  target_quad?: [number, number][]
  source_coverage?: number
  card_source_coverage?: number
  max_corner_shift_fraction?: number
}

export type CenteringProjectionStatus =
  | 'ok'
  | 'fallback'
  | 'partial'
  | 'unavailable'
  | 'invalid'

export interface CenteringProjectionSide {
  side: BorderEdge
  available: boolean
  status: CenteringProjectionStatus
  outer_line?: BorderLine | null
  inner_line?: BorderLine | null
}

export interface CenteringProjectionSection {
  fraction: number
  first_ratio: number
  second_ratio: number
  first_segment?: BorderLine
  second_segment?: BorderLine
  error?: number
}

export interface CenteringProjectionAxis {
  axis: 'horizontal' | 'vertical'
  available: boolean
  status: CenteringProjectionStatus
  labels: [BorderEdge, BorderEdge]
  sections: CenteringProjectionSection[]
  worst_index?: number | null
}

export interface CenteringProjection {
  version: 1 | 2
  coordinate_space: 'source_pixels' | 'rectified_pixels'
  image_shape: [number, number]
  status: CenteringProjectionStatus
  sides: Record<BorderEdge, CenteringProjectionSide>
  axes: {
    horizontal: CenteringProjectionAxis
    vertical: CenteringProjectionAxis
  }
  rectified_anchor_measurement?: {
    version?: 1
    available?: boolean
    status?: CenteringProjectionStatus
    coordinate_space: 'unit_card'
    outer_frame?: {
      available?: boolean
      source_quad?: [number, number][] | null
      condition?: number
      message?: string
    }
    axes: {
      horizontal: CenteringProjectionAxis
      vertical: CenteringProjectionAxis
    }
    message?: string
  } | null
  image_rectification?: ImageRectification | null
}

export interface CenteringProjectionGeometry {
  version: 1 | 2
  coordinate_space: 'source_pixels' | 'rectified_pixels'
  image_shape: [number, number]
  sides: Record<BorderEdge, { outer_line: BorderLine; inner_line: BorderLine }>
}

export interface BorderDetection {
  id: string
  side?: 'front' | 'back'
  edge: BorderEdge
  line?: BorderLine
  box?: DamageBox
  crop_box?: DamageBox
  centering_error?: number
  centering_ratio?: number
  width_pixels?: number
  angle?: number
  angle_degrees?: number
  raw_file_url?: string
  emboss_file_url?: string
  thumbnail_file_url?: string
}

export interface BorderWidthRatios {
  h: number
  v: number
}

export interface CenteringLineSettings {
  leftOuterRatio?: number
  rightOuterRatio?: number
  topOuterRatio?: number
  bottomOuterRatio?: number
  leftInnerRatio?: number
  rightInnerRatio?: number
  topInnerRatio?: number
  bottomInnerRatio?: number
}

export interface GradeSideAnalysis {
  sourceWidth: number
  sourceHeight: number
  damageMaskPaths?: string[]
  damageDetections?: DamageDetection[]
  borderDetections?: BorderDetection[]
  borderCoords?: Partial<Record<'left' | 'right' | 'top' | 'bottom', number[]>>
  borderWidthRatios: BorderWidthRatios
  borderErrorSides?: string[]
  centeringLines: CenteringLineSettings
  centeringProjection?: CenteringProjection
}

export interface GradeShareData {
  code: string
  previewImageUrl?: string
  gradedAt: string // ISO date
  user: {
    username: string
    pokemonId: number
    profileColor: string
    referralCode?: string
  }
  card: {
    name: string
    setName: string
    number: string
    officialCount: string
    language: string
    frontImageUrl: string
    backImageUrl: string
    frontImageRectificationApplied?: boolean
    backImageRectificationApplied?: boolean
    /** Emboss/surface-relief renders — power the Surface view toggle */
    embossFrontImageUrl?: string
    embossBackImageUrl?: string
    /** Fallback slab tint seed; the slab samples the front photo when it can */
    paletteColor?: string
  }
  providerGrades: Partial<Record<GradingProvider, ProviderGradeResult>>
  centering: { front: CenteringSide; back: CenteringSide }
  imperfections: { front: ImperfectionsSide; back: ImperfectionsSide }
  profit: Partial<Record<GradingProvider, ProfitInputs>>
  analysis?: { front: GradeSideAnalysis; back: GradeSideAnalysis }
  closest_match_summary?: ClosestMatchSummary | null
  most_similar_grade?: MostSimilarGrade | null
}
